import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from '@material-ui/core';
import RepartoChip from './RepartoChip';
import {
  INTERA_STRUTTURA,
  isRepartoPresente,
  isVisibilitaCompleta,
  nomiFuoriCartella,
} from './visibilitaAllegati';

export const SELEZIONA_ALMENO_UN_REPARTO = 'Seleziona almeno un reparto.';
export const REPARTI_FUORI_CARTELLA =
  'Togli i reparti segnati: non vedono la cartella di destinazione.';
export const REPARTI_IN_LETTURA = 'Lettura dei reparti in corso…';
export const REPARTI_NON_LEGGIBILI =
  "Impossibile leggere i reparti dell'esercizio: la visibilità non si può modificare.";
export const NESSUN_REPARTO =
  "L'esercizio non ha reparti oltre a quello predefinito: resta visibile all'intera struttura.";
export const LIMITE_CARTELLA =
  'Solo i reparti che vedono la cartella di destinazione.';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  // The same shape as the «Cartella di destinazione» label beside it in the
  // attachment form: one section label per block, so the form reads as a list
  // of blocks and not as a label in two different voices.
  legenda: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  lettura: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
}));

/**
 * Who sees an attachment or a folder: the whole structure, or one or more
 * reparti of the esercizio. Controlled: `value` is `{ ristretta, reparti }` as
 * `visibilitaAllegati` defines it, and `onChange` gets the next one.
 *
 * `reparti` is the read of the selectable reparti with its state, because the
 * three states read differently to the operator: while it loads nothing can be
 * chosen yet; when it failed the field says so and holds still, so a title can
 * still be saved without touching the visibility; when it is empty the only
 * visibility is the whole structure.
 *
 * `repartiAmmessi` narrows the options to the reparti that see the destination
 * folder: a restriction to a reparto the folder hides from would be visible to
 * nobody but the capo. A reparto already in the value that the folder hides
 * from — chosen before the folder changed, or saved before the folder was
 * restricted — stays, flagged, and the field is in error until it is taken
 * out: the callers do not save a value that is not `isVisibilitaAmmessa`.
 *
 * Options are matched to the value by name. A reparto in the value whose name
 * is not among the options is kept, shown as missing, and sent back with its id
 * on save — the server still honours it, and dropping it would widen the
 * visibility without anyone asking for that.
 */
export default function VisibilitaField({
  id,
  value,
  onChange,
  reparti,
  repartiAmmessi,
  disabled,
}) {
  const classes = useStyles();
  const pronti = reparti.stato === 'pronto';
  const bloccato = disabled || !pronti;
  const nessunReparto = pronti && reparti.lista.length === 0;
  const completa = isVisibilitaCompleta(value);

  const nomiSelezionati = [...new Set(value.reparti.map((r) => r.nome))];
  const isOrfano = (nome) => pronti && !isRepartoPresente(nome, reparti.lista);
  const fuoriCartella = nomiFuoriCartella(
    value,
    repartiAmmessi,
    pronti ? reparti.lista : null,
  );
  const ammessa = fuoriCartella.length === 0;
  // A missing reparto is flagged as missing and nothing else: it hides the
  // element from everyone on its own, whatever the folder says.
  const avvisoPer = (nome) => {
    if (isOrfano(nome)) return 'nonPresente';
    if (fuoriCartella.includes(nome)) return 'fuoriCartella';
    return null;
  };
  const opzioni = pronti
    ? reparti.lista.filter(
        (reparto) =>
          !repartiAmmessi || isRepartoPresente(reparto.nome, repartiAmmessi),
      )
    : [];

  const handleScelta = (event) => {
    if (event.target.value === 'intera') {
      onChange(INTERA_STRUTTURA);
    } else {
      onChange({ ristretta: true, reparti: value.reparti });
    }
  };

  // From names back to reparti: the entry already in the value first, so a
  // stored id survives a toggle of some other reparto; else the selectable copy.
  const repartoPerNome = (nome) =>
    value.reparti.find((r) => r.nome === nome) ||
    reparti.lista.find((r) => r.nome === nome);
  const emettiNomi = (nomi) =>
    onChange({
      ristretta: true,
      reparti: nomi.map(repartoPerNome).filter(Boolean),
    });
  const handleSelezione = (event) => emettiNomi(event.target.value);
  const rimuovi = (nome) =>
    emettiNomi(nomiSelezionati.filter((altro) => altro !== nome));

  const legendaId = `${id}-legenda`;
  const labelId = `${id}-reparti-label`;

  let statoLettura = null;
  if (reparti.stato === 'caricamento') {
    statoLettura = (
      <span className={classes.lettura}>
        <CircularProgress size={14} aria-hidden />
        {REPARTI_IN_LETTURA}
      </span>
    );
  } else if (reparti.stato === 'errore') {
    statoLettura = REPARTI_NON_LEGGIBILI;
  } else if (nessunReparto) {
    statoLettura = NESSUN_REPARTO;
  }

  return (
    <FormControl
      component="fieldset"
      className={classes.root}
      disabled={bloccato}
    >
      <FormLabel component="legend" id={legendaId} className={classes.legenda}>
        Visibilità
      </FormLabel>
      <RadioGroup
        row
        aria-labelledby={legendaId}
        name={`${id}-scelta`}
        value={value.ristretta ? 'reparti' : 'intera'}
        onChange={handleScelta}
      >
        <FormControlLabel
          value="intera"
          control={<Radio color="primary" />}
          label="Intera struttura"
        />
        <FormControlLabel
          value="reparti"
          control={<Radio color="primary" />}
          label="Reparti specifici"
          // With no reparto to choose from, the choice is not there; a value
          // already restricted still shows, so it can be read and undone.
          disabled={bloccato || (nessunReparto && !value.ristretta)}
        />
      </RadioGroup>
      {value.ristretta && (
        <FormControl
          variant="outlined"
          fullWidth
          size="small"
          disabled={bloccato}
          error={!completa || !ammessa}
        >
          <InputLabel id={labelId}>Reparti</InputLabel>
          <Select
            labelId={labelId}
            label="Reparti"
            multiple
            value={nomiSelezionati}
            onChange={handleSelezione}
            renderValue={(nomi) => (
              <div className={classes.chips}>
                {nomi.map((nome) => (
                  <RepartoChip
                    key={nome}
                    nome={nome}
                    avviso={avvisoPer(nome)}
                    onDelete={bloccato ? undefined : () => rimuovi(nome)}
                  />
                ))}
              </div>
            )}
            MenuProps={{
              getContentAnchorEl: null,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
            }}
          >
            {opzioni.map((reparto) => (
              <MenuItem key={reparto.nome} value={reparto.nome}>
                <Checkbox
                  color="primary"
                  checked={nomiSelezionati.includes(reparto.nome)}
                />
                <ListItemText primary={reparto.nome} />
              </MenuItem>
            ))}
          </Select>
          {!completa && (
            <FormHelperText>{SELEZIONA_ALMENO_UN_REPARTO}</FormHelperText>
          )}
          {completa && !ammessa && (
            <FormHelperText>{REPARTI_FUORI_CARTELLA}</FormHelperText>
          )}
          {completa && ammessa && repartiAmmessi && (
            <FormHelperText>{LIMITE_CARTELLA}</FormHelperText>
          )}
        </FormControl>
      )}
      {/* A `div`, not the default `<p>`: the spinner that reads out the
          loading state is a block, and a block inside a paragraph is markup
          the browser reopens somewhere else. */}
      {statoLettura && (
        <FormHelperText component="div">{statoLettura}</FormHelperText>
      )}
    </FormControl>
  );
}

const repartoShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  nome: PropTypes.string.isRequired,
});

VisibilitaField.propTypes = {
  id: PropTypes.string,
  value: PropTypes.shape({
    ristretta: PropTypes.bool.isRequired,
    reparti: PropTypes.arrayOf(repartoShape).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  reparti: PropTypes.shape({
    stato: PropTypes.oneOf(['caricamento', 'pronto', 'errore']).isRequired,
    lista: PropTypes.arrayOf(repartoShape).isRequired,
  }).isRequired,
  repartiAmmessi: PropTypes.arrayOf(repartoShape),
  disabled: PropTypes.bool,
};

VisibilitaField.defaultProps = {
  id: 'visibilita',
  repartiAmmessi: null,
  disabled: false,
};
